import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiCheckCircle, FiDollarSign, FiClock, FiCamera, FiX, FiImage } = FiIcons;

const Payouts = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [receipts, setReceipts] = useState({});

  useEffect(() => {
    if (user) fetchPayoutData();
  }, [user]);

  const fetchPayoutData = async () => {
    setLoading(true);
    try {
      const { data: pilots } = await supabase.from(TABLES.USERS).select('id, username').eq('master_id', user.id);
      const { data: logs } = await supabase.from(TABLES.LOGS).select('pilot_id, value_at_log').eq('master_id', user.id).eq('status', 'unpaid');
      
      const summary = pilots?.map(p => {
        const total = logs?.filter(l => l.pilot_id === p.id).reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
        return { ...p, totalUnpaid: total };
      }) || [];
      
      setPayouts(summary);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (pilotId, e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
      setReceipts(prev => ({ ...prev, [pilotId]: { file, preview: URL.createObjectURL(file) } }));
    }
  };

  const clearReceipt = (pilotId) => {
    setReceipts(prev => {
      const updated = { ...prev };
      delete updated[pilotId];
      return updated;
    });
  };

  const markAsPaid = async (pilotId) => {
    if (!confirm('Settle all pending logs for this pilot?')) return;
    setProcessingId(pilotId);
    let paymentProofUrl = null;

    try {
      const receipt = receipts[pilotId];
      if (receipt) {
        const fileExt = receipt.file.name.split('.').pop();
        const fileName = `${Date.now()}-${pilotId}.${fileExt}`;
        const filePath = `payout-receipts/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('payout-proofs')
          .upload(filePath, receipt.file);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('payout-proofs')
          .getPublicUrl(filePath);
        paymentProofUrl = publicUrl;
      }

      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({ status: 'paid', payment_proof_url: paymentProofUrl })
        .eq('master_id', user.id)
        .eq('pilot_id', pilotId)
        .eq('status', 'unpaid');

      if (error) throw error;
      
      clearReceipt(pilotId);
      fetchPayoutData();
      alert('Payout settled successfully!');
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <header>
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Payout Management</h2>
        <p className="text-gray-500 font-medium">Review and settle pending drop payments.</p>
      </header>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {payouts.map(p => (
          <div key={p.id} className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col hover:shadow-xl transition-all h-full">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm">
                  <FiDollarSign className="text-xl" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xl">{p.username}</h3>
                  <div className="flex items-center gap-1.5 text-gray-400 text-[10px] font-black uppercase tracking-widest">
                    <FiClock /> Pending Settlement
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <p className="text-4xl md:text-5xl font-black text-indigo-600 tracking-tighter">
                  ₱{p.totalUnpaid.toLocaleString()}
                </p>
              </div>

              {p.totalUnpaid > 0 && (
                <div className="mb-6 space-y-3">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Upload Receipt (Optional)</label>
                  {!receipts[p.id] ? (
                    <div className="relative">
                      <input type="file" id={`receipt-${p.id}`} className="hidden" accept="image/*" onChange={(e) => handleFileChange(p.id, e)} />
                      <label htmlFor={`receipt-${p.id}`} className="flex items-center justify-center gap-2 w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:border-indigo-400 hover:text-indigo-500 cursor-pointer transition-all">
                        <FiCamera />
                        <span className="text-xs font-bold uppercase">Attach Proof</span>
                      </label>
                    </div>
                  ) : (
                    <div className="relative rounded-2xl overflow-hidden border-2 border-indigo-100 group aspect-video">
                      <img src={receipts[p.id].preview} alt="Receipt" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => clearReceipt(p.id)} className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full backdrop-blur-md hover:bg-red-500 transition-all opacity-0 group-hover:opacity-100">
                        <FiX />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button 
              type="button"
              onClick={() => markAsPaid(p.id)} 
              disabled={p.totalUnpaid === 0 || processingId === p.id} 
              className="w-full py-5 bg-emerald-600 text-white rounded-3xl font-black text-sm hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400 transition-all flex items-center justify-center gap-3 shadow-xl shadow-emerald-100 disabled:shadow-none"
            >
              {processingId === p.id ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <><FiCheckCircle className="text-xl" /> Mark as Settled</>
              )}
            </button>
          </div>
        ))}
      </div>
      
      {payouts.length === 0 && !loading && (
        <div className="py-20 text-center text-gray-300 font-black uppercase tracking-widest">No pilots registered under your command.</div>
      )}
    </div>
  );
};

export default Payouts;